import { useEffect } from 'react';
import { toast } from 'sonner';

/**
 * "A newer version is available" notice for tabs that stay open across a
 * deploy.
 *
 * The build writes its version into `/version.json` (see `vite.config.js`)
 * and bakes the same value into the bundle as `__BUILD_VERSION__`. A tab
 * compares the two now and then; when the deployed one is newer it shows a
 * toast with a reload button. Nothing reloads on its own — unsaved input
 * stays the user's call.
 *
 * Because `version.json` ships in the same Hosting release as the chunks,
 * the notice can never announce a version whose files are not there yet.
 */

const VERSION_URL = '/version.json';
const TOAST_ID = 'new-version-available';

/** How often a visible tab asks for the deployed version. */
export const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;

/** True when the deployed build is newer than the one running in this tab. */
export function isNewerVersion(deployed: unknown, running: string): boolean {
  if (typeof deployed !== 'string' || deployed === '') return false;
  const deployedAt = Number(deployed);
  const runningAt = Number(running);
  return Number.isFinite(deployedAt) && Number.isFinite(runningAt) && deployedAt > runningAt;
}

/** Reads the deployed version; `null` when it cannot be determined. */
export async function fetchDeployedVersion(fetchFn: typeof fetch = fetch): Promise<string | null> {
  try {
    const response = await fetchFn(VERSION_URL, { cache: 'no-store' });
    if (!response.ok) return null;
    const body: unknown = await response.json();
    const version = (body as { version?: unknown } | null)?.version;
    return typeof version === 'string' ? version : null;
  } catch {
    return null;
  }
}

interface WatchOptions {
  running: string;
  onNewVersion: () => void;
  fetchVersion?: () => Promise<string | null>;
}

/**
 * Checks on start, every {@link VERSION_CHECK_INTERVAL_MS} and whenever the
 * tab becomes visible again. Calls `onNewVersion` once, then stops asking.
 * Returns a function that stops the watch.
 */
export function watchForNewVersion({
  running,
  onNewVersion,
  fetchVersion = fetchDeployedVersion,
}: WatchOptions): () => void {
  let done = false;

  const stop = () => {
    done = true;
    window.clearInterval(interval);
    document.removeEventListener('visibilitychange', check);
  };

  async function check() {
    if (done || document.visibilityState !== 'visible') return;
    const deployed = await fetchVersion();
    if (done || !isNewerVersion(deployed, running)) return;
    stop();
    onNewVersion();
  }

  const interval = window.setInterval(() => void check(), VERSION_CHECK_INTERVAL_MS);
  document.addEventListener('visibilitychange', check);
  void check();

  return stop;
}

function showNewVersionNotice() {
  toast('Neue Version verfügbar', {
    id: TOAST_ID,
    description: 'Bitte neu laden, um mit dem aktuellen Stand zu arbeiten.',
    duration: Infinity,
    action: { label: 'Neu laden', onClick: () => window.location.reload() },
  });
}

export function useNewVersionNotice() {
  useEffect(() => {
    // The dev server has no version.json and rebuilds on every change anyway.
    if (import.meta.env.DEV) return;
    return watchForNewVersion({ running: __BUILD_VERSION__, onNewVersion: showNewVersionNotice });
  }, []);
}
